import {
  calculateEffectiveServiceHoursForEntry,
  getPersistedPauseHours,
} from '../../config/logbookEntryRules.js';
import { getComplianceLightPdfColor } from '../../config/complianceLightStatus.js';
import { formatBerlinDate, formatBerlinDateTime } from './pdfDateTime';

const PDF_BRANDING_ASSET_PATHS = {
  feuerwehrLogo: '/feuerwehr_duesseldorf.png',
  hoehenrettungLogo: '/logo.jpeg',
};

const DAY_SERVICE_SCALE_FACTOR = 1.17;
const DAY_SERVICE_ENTRY_TYPES = new Set([
  'Tagesfortbildung',
  'Tag einer Wochenfortbildung',
  'Pflichtfortbildung',
  'Tag eines Wettkampfes',
  'Trainingstag für einen Wettkampf',
  'Dozententätigkeit (Tagesdienst)',
  'Geräteprüfung (Tagesdienst)',
  'Ausbilderfortbildung (Intern)',
  'Schulung durch externen Ausbilder',
  'Externe Fortbildung',
  'Fortbildung Geräteprüfer',
  'Honorartätigkeit',
]);

let brandingAssetsPromise;

function convertBlobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function loadBrandingImage(assetPath) {
  const basePath = process.env.PUBLIC_URL || '';
  const response = await fetch(`${basePath}${assetPath}`);
  if (!response.ok) {
    throw new Error(`Asset konnte nicht geladen werden: ${assetPath}`);
  }

  return convertBlobToDataUrl(await response.blob());
}

export async function loadLogbuchPdfBrandingAssets() {
  if (!brandingAssetsPromise) {
    brandingAssetsPromise = Promise.all(
      Object.entries(PDF_BRANDING_ASSET_PATHS).map(async ([key, path]) => {
        try {
          const dataUrl = await loadBrandingImage(path);
          return [key, dataUrl];
        } catch {
          return [key, null];
        }
      }),
    ).then((entries) => Object.fromEntries(entries));
  }

  return brandingAssetsPromise;
}

function formatHours(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }

  const totalMinutes = Math.round(numeric * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} h`;
}

function formatHoursTight(value) {
  const formatted = formatHours(value);
  return formatted === '-' ? formatted : formatted.replace(' h', 'h');
}

function scaleValueForEntryType(entryType, value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }

  if (!DAY_SERVICE_ENTRY_TYPES.has(String(entryType || '').trim())) {
    return numeric;
  }

  return Number((numeric * DAY_SERVICE_SCALE_FACTOR).toFixed(2));
}

function buildAmpel(color, label) {
  return {
    columns: [
      {
        canvas: [
          {
            type: 'ellipse',
            x: 10,
            y: 10,
            color,
            r1: 8,
            r2: 8,
          },
        ],
        width: 24,
      },
      {
        text: label,
        margin: [0, 0, 0, 0],
        fontSize: 11,
        bold: true,
        color: '#333',
        alignment: 'left',
      },
    ],
    margin: [0, 0, 0, 4],
  };
}

export function buildLogbuchPdfHeader({ assets = {}, title = 'Stundenprotokoll' } = {}) {
  return {
    columns: [
      {
        width: '*',
        stack: [
          ...(assets.feuerwehrLogo ? [{ image: assets.feuerwehrLogo, width: 220, margin: [0, 0, 0, 10] }] : []),
          { text: title, style: 'brandTitle' },
          { text: 'Höhenrettung Feuerwehr Düsseldorf', style: 'brandSubtitle' },
        ],
      },
      {
        width: 86,
        stack: assets.hoehenrettungLogo ? [{ image: assets.hoehenrettungLogo, width: 74, alignment: 'right' }] : [],
      },
    ],
    columnGap: 14,
    margin: [0, 0, 0, 14],
  };
}

export function buildLogbuchPdfStyles() {
  return {
    brandTitle: { fontSize: 21, bold: true, color: '#1f2937', margin: [0, 0, 0, 2] },
    brandSubtitle: { fontSize: 11, color: '#6b7280', margin: [0, 0, 0, 2] },
    metaName: { fontSize: 13, bold: true, color: '#222' },
    title: { fontSize: 16, bold: true, color: '#222', margin: [0, 0, 0, 2] },
    header: { fontSize: 14, bold: true, margin: [0, 0, 0, 10] },
    meta: { fontSize: 11, color: '#333' },
    sum: { fontSize: 12, bold: true, color: '#222' },
    timestamp: { fontSize: 9, color: '#888' },
  };
}

export function buildLogbuchPdfDocument({
  fullName,
  year,
  entries = [],
  complianceLights = [],
  exportDate = new Date(),
  periodText = null,
  brandingAssets = {},
  headerTitle = 'Logbuch',
}) {
  const tableHeader = ['Datum', 'HöRett Stunden', 'Art', 'Zeitraum', 'Pause', 'Vor-/Nachbereitung', 'Dienstzeit', 'Verantwortlicher'];

  const tableBody = entries.map((entry) => {
    const pauseHours = Number.isFinite(Number(entry?.applied_pause_hours))
      ? Number(entry.applied_pause_hours || 0)
      : getPersistedPauseHours(entry);
    const effectiveServiceHours = typeof entry?.service_time !== 'undefined' && entry?.service_time !== null
      ? Number(entry.service_time || 0)
      : calculateEffectiveServiceHoursForEntry(entry);
    const prepHours = Number(entry?.prep_time || 0);

    return [
      formatBerlinDate(entry.date),
      formatHours(entry.hours || 0),
      entry.entry_type || '-',
      entry.time_from && entry.time_to ? `${entry.time_from} - ${entry.time_to}` : '-',
      formatHours(pauseHours),
      formatHours(prepHours),
      formatHours(effectiveServiceHours),
      entry.responsible_name || '-',
    ];
  });

  const sumAll = entries.reduce((sum, entry) => sum + Number(entry.hours || 0), 0);
  const sumLehr = entries.reduce((sum, entry) => sum + Number(entry.lehrh_stunden || 0), 0);
  const sumPause = entries.reduce((sum, entry) => {
    const pauseHours = Number.isFinite(Number(entry?.applied_pause_hours))
      ? Number(entry.applied_pause_hours || 0)
      : getPersistedPauseHours(entry);
    return sum + pauseHours;
  }, 0);
  const sumService = entries.reduce((sum, entry) => {
    const serviceHours = typeof entry?.service_time !== 'undefined' && entry?.service_time !== null
      ? Number(entry.service_time || 0)
      : calculateEffectiveServiceHoursForEntry(entry);
    return sum + (Number.isFinite(serviceHours) ? serviceHours : 0);
  }, 0);
  const sumPrep = entries.reduce((sum, entry) => sum + Number(entry?.prep_time || 0), 0);
  const scaledSumPause = entries.reduce((sum, entry) => {
    const pauseHours = Number.isFinite(Number(entry?.applied_pause_hours))
      ? Number(entry.applied_pause_hours || 0)
      : getPersistedPauseHours(entry);
    return sum + scaleValueForEntryType(entry?.entry_type, pauseHours);
  }, 0);
  const scaledSumService = entries.reduce((sum, entry) => {
    const serviceHours = typeof entry?.service_time !== 'undefined' && entry?.service_time !== null
      ? Number(entry.service_time || 0)
      : calculateEffectiveServiceHoursForEntry(entry);
    return sum + scaleValueForEntryType(entry?.entry_type, Number.isFinite(serviceHours) ? serviceHours : 0);
  }, 0);
  const scaledSumPrep = entries.reduce(
    (sum, entry) => sum + scaleValueForEntryType(entry?.entry_type, Number(entry?.prep_time || 0)),
    0,
  );

  const ampelArr = complianceLights.map((light) => (
    buildAmpel(getComplianceLightPdfColor(light), light.label)
  ));

  const yearLabel = Number.isInteger(Number(year)) ? String(year) : '-';

  return {
    pageOrientation: 'landscape',
    content: [
      buildLogbuchPdfHeader({ assets: brandingAssets, title: headerTitle }),
      {
        columns: [
          { text: [{ text: 'Name: ', bold: true }, { text: fullName || '-', bold: true }], style: 'metaName' },
          { text: [{ text: 'Jahr: ', bold: true }, { text: yearLabel, bold: false }], style: 'meta', alignment: 'right' },
        ],
        margin: [0, 0, 0, 6],
      },
      ...(periodText ? [{ text: periodText, style: 'meta', margin: [0, 0, 0, 4] }] : []),
      ...ampelArr,
      { text: ' ', margin: [0, 0, 0, 6] },
      {
        table: {
          headerRows: 1,
          widths: ['auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto', '*'],
          body: [tableHeader, ...tableBody],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 8, 0, 8],
      },
      { text: `Summe aller HöRett Stunden: ${formatHours(sumAll)}`, style: 'sum', margin: [0, 8, 0, 0] },
      { text: `Summe aller HöRett Stunden mit Lehrhöhenretter: ${formatHours(sumLehr)}`, style: 'sum', margin: [0, 0, 0, 0] },
      {
        text: `Summe aller Dienstzeiten: ${formatHoursTight(sumService)} (Skaliert: ${formatHoursTight(scaledSumService)})`,
        style: 'sum',
        margin: [0, 0, 0, 0],
      },
      {
        text: `Summe aller Vor-/Nachbereitungen: ${formatHoursTight(sumPrep)} (Skaliert: ${formatHoursTight(scaledSumPrep)})`,
        style: 'sum',
        margin: [0, 0, 0, 0],
      },
      {
        text: `Summe aller Pausenzeiten: ${formatHoursTight(sumPause)} (Skaliert: ${formatHoursTight(scaledSumPause)})`,
        style: 'sum',
        margin: [0, 0, 0, 12],
      },
      { text: `Exportiert am: ${formatBerlinDateTime(exportDate)}`, style: 'timestamp', alignment: 'right' },
    ],
    styles: buildLogbuchPdfStyles(),
  };
}
